<template>
  <div class="stripe-element">
    <div class="stripe-element__element" ref="element"></div>
    <div class="stripe-element__error" v-if="formErrors.stripe">
      {{ formErrors.stripe }}
    </div>
    <button
      v-if="hasFocus"
      @focus="proxyFocus"
      :style="{
        display: 'block',
        border: '1px solid transparent',
        width: 0,
        height: 0,
        padding: 0,
      }"
    ></button>
    <div v-else :style="{ height: '2px' }"></div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

import i18n from '../../i18n';
import { FOCUSABLE_ELEMENTS } from '../../directives/focus';

export default {
  props: {
    type: String,
    options: Object,
    autoFocus: Boolean,
  },
  data() {
    return {
      hasFocus: false,
    };
  },
  computed: {
    ...mapState(['theme', 'stripeInstance']),
    ...mapState('checkoutForm', ['formErrors']),
  },
  methods: {
    ...mapActions(['addStripeElement']),
    ...mapActions('checkoutForm', ['setFormValue', 'setFormError']),
    proxyFocus(e) {
      // the following is necessary because in some cases when tabbing out of the
      // stripe element the focus will be applied to an out-of-order element
      // the hidden button (which consistently receives focus) acts as a proxy
      const page = e.target.closest('.page');
      if (!page) return;
      const elements = [...page.querySelectorAll(FOCUSABLE_ELEMENTS)];
      for (let i = elements.indexOf(e.target) + 1; i < elements.length; i++) {
        const element = elements[i];
        if (element && element.checkVisibility()) {
          e.preventDefault();
          element.focus();
          break;
        }
      }
    },
  },
  mounted() {
    if (!this.stripeInstance) {
      this.setFormError({
        name: 'stripe',
        value: i18n.gettext(
          'Error loading Stripe - please choose another payment method'
        ),
      });
      return;
    }

    const element = this.stripeInstance.elements().create(this.type, {
      style: {
        base: {
          color: this.theme.textColor,
          '::placeholder': {
            color: this.theme.lineColor,
          },
          iconColor: this.theme.textColor,
          fontSize: '16px',
        },
        invalid: {
          color: this.theme.errorColor,
          iconColor: this.theme.errorColor,
        },
      },
      ...this.options,
    });
    element.addEventListener('ready', () => {
      if (!this.autoFocus) return;
      const input = this.$refs.element.getElementsByTagName('input')[0];
      if (input) {
        input.focus();
      }
      this.setFormValue({
        name: 'stripe',
        value: false,
      });
    });
    element.addEventListener('change', (e) => {
      if (e.error) {
        this.setFormError({
          name: 'stripe',
          value: e.error.message,
        });
      } else if (e.complete) {
        this.setFormValue({
          name: 'stripe',
          value: true,
        });
      }
    });
    element.addEventListener('focus', () => {
      this.hasFocus = true;
    });
    element.addEventListener('blur', (e) => {
      this.hasFocus = false;
    });
    element.mount(this.$refs.element);
    this.addStripeElement({ type: this.type, element });
  },
  beforeDestroy() {
    this.setFormValue({
      name: 'stripe',
      value: null,
    });
  },
};
</script>

<style>
.stripe-element {
  margin-bottom: 1rem;
}
.stripe-element__element {
  background-color: var(--app-theme__menuColor);
  padding: 1rem;
}
.stripe-element__error {
  color: var(--app-theme__errorColor);
  padding-top: 0.25rem;
}
</style>
